import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from '../../../utils/dayjs';
import get from 'lodash/get';
import {
  toolIsManuallyGraded,
  formatDisplayPoints,
  isLearningCurve,
} from '@macmillan-learning/gradebook-shared';
import MLDialog from '../../MLDialog/MLDialog';
import MLButton from '../../MLButton/MLButton';
import MLTooltip from '../../MLTooltip';
import s from './OverrideGradeDialog.module.css';
import { round } from '../../../utils/util';
import { sendAnalyticsEvent } from '../../../actions/sendAnalytics';
import {
  clickSaveGradeChanges,
  viewAssignmentReport,
  viewStudentAssignment,
} from '../../../actions/analyticsEvents';

function formatGrade(grade, toFixedAmount = 2) {
  const formatted = parseFloat(grade);
  if (isNaN(formatted)) {
    return round(parseFloat(0), toFixedAmount);
  }
  return round(formatted, toFixedAmount);
}

function getAssignmentForDisplay(gradeOverrideData) {
  const dueDate = gradeOverrideData?.dueDate;
  return {
    ...gradeOverrideData,
    id: gradeOverrideData.assignmentId,
    past_due: dueDate ? new Date() > new Date(dueDate) : false,
  };
}

function getDisplayGradeFromOverrideData(
  gradeOverrideData,
  rdxGradebookSettings,
  ignoreDecimalZeros = false,
) {
  return formatDisplayPoints(
    getAssignmentForDisplay(gradeOverrideData),
    rdxGradebookSettings,
    {
      precision: 2,
      ignoreDecimalZeros,
    },
  );
}

function hasExistingGrade(gradeOverrideData) {
  const { studentGrade, studentPoints, isInstructorEntered } =
    gradeOverrideData;
  return (
    isInstructorEntered ||
    (studentGrade != null && Number.isFinite(studentGrade)) ||
    (studentPoints != null && Number.isFinite(studentPoints))
  );
}

function getEditableGradeValue(gradeOverrideData, rdxGradebookSettings) {
  if (!hasExistingGrade(gradeOverrideData)) {
    return '';
  }

  return formatDisplayPoints(
    getAssignmentForDisplay(gradeOverrideData),
    rdxGradebookSettings,
    {
      precision: 2,
      ignoreDecimalZeros: true,
      forceShowGrade: true,
    },
  );
}

class OverrideGradeDialog extends Component {
  static propTypes = {
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    dispatch: PropTypes.func,
    show: PropTypes.bool,
    gradeOverrideData: PropTypes.object,
    courseId: PropTypes.string,
    rdxGradebookSettings: PropTypes.object,
    disableEnforceFocus: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    const comment = get(props, 'gradeOverrideData.gradeComment', '');
    const { gradeOverrideData, rdxGradebookSettings } = props;
    const grade = getEditableGradeValue(
      gradeOverrideData,
      rdxGradebookSettings,
    );

    this.initialGrade = grade;
    this.initialComment = comment || '';

    this.state = {
      grade,
      comment: this.initialComment,
      gradeHasChanges: false,
      commentHasChanges: false,
    };
  }

  handleGradeChange(event) {
    const newGrade = event.target.value;
    this.setState({
      grade: newGrade,
      gradeHasChanges: newGrade !== this.initialGrade,
    });
  }

  handleCommentChange(event) {
    const newComment = event.target.value;
    this.setState({
      comment: newComment,
      commentHasChanges: newComment !== this.initialComment,
    });
  }

  render() {
    const { grade, comment } = this.state;
    const {
      onClose,
      onSubmit,
      show,
      gradeOverrideData,
      courseId,
      dispatch,
      rdxGradebookSettings,
      disableEnforceFocus,
    } = this.props;
    const { userName } = gradeOverrideData;
    const assignedPoints = formatGrade(gradeOverrideData.assignedPoints);
    const dueDate = gradeOverrideData && gradeOverrideData.dueDate;
    const dateCompleted = gradeOverrideData && gradeOverrideData.dateCompleted;
    const gradeComment =
      (gradeOverrideData && gradeOverrideData.gradeComment) || '';
    const hasStudentReport =
      gradeOverrideData && gradeOverrideData.hasStudentReport;
    const studentAssignmentLink =
      gradeOverrideData && gradeOverrideData.studentAssignmentLink;
    const studentReportLink =
      gradeOverrideData && gradeOverrideData.studentReportLink;
    const assignmentIsLearningCurve = isLearningCurve(gradeOverrideData.tool);
    const isInstructorEntered = gradeOverrideData.isInstructorEntered;
    const hasGradeComment = gradeOverrideData && comment !== gradeComment;
    const hasGradeOverride =
      gradeOverrideData &&
      grade !== gradeOverrideData.studentPoints?.toString();
    const displayGrade = getDisplayGradeFromOverrideData(
      gradeOverrideData,
      rdxGradebookSettings,
    );

    const formHasChanges =
      this.state.gradeHasChanges || this.state.commentHasChanges;

    const actions = [
      <MLButton
        key="cancel"
        btnType="secondary"
        testId="cancel_grade_override"
        onClick={onClose}
      >
        Cancel
      </MLButton>,
      <MLButton
        key="save"
        btnType="primary"
        disabled={!formHasChanges}
        testId="save_grade_override"
        onClick={() => {
          onSubmit(this.state);
          dispatch(
            sendAnalyticsEvent(
              clickSaveGradeChanges(
                courseId,
                hasGradeComment,
                hasGradeOverride,
              ),
            ),
          );
        }}
      >
        Save
      </MLButton>,
    ];

    return (
      <MLDialog
        className={s.modalWindow}
        title={`${userName} - Grade Details`}
        actions={actions}
        show={show}
        onClose={onClose}
        actionsClassName={s.actions}
        testId="grade-override-dialog"
        disableEnforceFocus={disableEnforceFocus}
      >
        <div className={s.top}>
          <MLTooltip content={gradeOverrideData.assignmentName}>
            <div className={s.title}>{gradeOverrideData.assignmentName}</div>
          </MLTooltip>
          <div className={s.linkList}>
            {studentAssignmentLink && (
              <div className={`${s.content} ${s.value}`}>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  data-test-id="view_assignment_grade_override"
                  href={studentAssignmentLink}
                  onClick={() => {
                    dispatch(
                      sendAnalyticsEvent(viewStudentAssignment(courseId)),
                    );
                  }}
                >
                  View Student Assignment
                </a>
              </div>
            )}
            {studentAssignmentLink && hasStudentReport && (
              <div className={s.seperator}>&nbsp;&nbsp;|&nbsp;&nbsp;</div>
            )}
            {hasStudentReport && (
              <div className={`${s.content} ${s.value}`}>
                <a
                  rel="noopener noreferrer"
                  data-test-id="view_report_grade_override"
                  href={studentReportLink}
                  onClick={() => {
                    dispatch(
                      sendAnalyticsEvent(viewAssignmentReport(courseId)),
                    );
                  }}
                >
                  View Assignment Report
                </a>
              </div>
            )}
          </div>
          <div className={s.rightTop}>
            {dueDate && (
              <div
                className={`${s.content} ${s.dateStyle}`}
                data-test-id="assignment_due_date"
              >
                <span>Due Date:</span>
                <span className={s.dateContent}>
                  {dayjs(dueDate).format('MMM. D, YYYY h:mm a')}
                </span>
              </div>
            )}
            {dateCompleted && (
              <div>
                <div
                  className={`${s.content} ${s.dateStyle}`}
                  data-test-id="assignment-completed"
                >
                  <>
                    {!assignmentIsLearningCurve && <span>Completed:</span>}
                    {assignmentIsLearningCurve && (
                      <span>Target Score Reached:</span>
                    )}
                    <span className={s.dateContent}>
                      {dayjs(dateCompleted).format('MMM. D, YYYY h:mm a')}
                    </span>
                  </>
                  {(isInstructorEntered ||
                    toolIsManuallyGraded(
                      gradeOverrideData.tool,
                      gradeOverrideData.subtype,
                    )) && (
                    <span
                      className={s.dateContent}
                      data-test-id="grade-override"
                    >
                      <br />
                      instructor entered
                    </span>
                  )}
                </div>
              </div>
            )}
            <div
              className={`${s.content} ${s.dateStyle}`}
              data-test-id="due_date_grade_override"
            >
              <span>Original Grade:&nbsp;</span>
              <span
                className={s.dateContent}
              >{`${displayGrade}/${assignedPoints} pts`}</span>
            </div>
          </div>
        </div>
        <div className={s.mid}>
          <div className={s.rightMid}>
            <div className={s.content}>
              <div className={s.inputGroup}>
                <label htmlFor="grade-input" className={s.headerText}>
                  {`Points out of ${assignedPoints}`}
                </label>
                <input
                  id="grade-input"
                  type="number"
                  className={s.grade}
                  label="__overrideGradeDialog_grade"
                  data-test-id="grade_edit_grade_override"
                  value={grade}
                  onChange={(e) => {
                    this.handleGradeChange(e);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={s.bottom}>
          <div className={s.inputGroup}>
            <label htmlFor="comment-input" className={s.headerText}>
              Add Note for Student
            </label>
            <textarea
              id="comment-input"
              className={s.comment}
              maxLength={300}
              label="__overrideGradeDialog_comment"
              data-test-id="comment_grade_override"
              value={comment}
              onChange={(e) => {
                this.handleCommentChange(e);
              }}
            />
          </div>
        </div>
      </MLDialog>
    );
  }
}

const mapStateToProps = (state) => ({
  showIndividualStudentGradebook:
    state?.gradebook?.showIndividualStudentGradebook,
});

export default connect(mapStateToProps)(OverrideGradeDialog);
